<!--
	Hero-lab V3 · SCAN PASS — the real scan (panel.glb) under a sweeping blue
	laser sheet (see $lib/three/ScanScene.ts). Same loading contract as Morph:
	three.js code-split, idle init, poster fallback for reduced-motion / no
	WebGL, canvas cross-fades in on the first frame. Runs only on screen.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { motionDisabled } from '$lib/motion/reduced';
	import type { ScanScene, ScanSceneOptions } from '$lib/three/ScanScene';

	let {
		/** Which part is under the scanner. Defaults to the homepage's MB fender. */
		options,
		poster = '/img/scan-poster.webp',
		hud = 'MB FENDER · STRUCTURED LIGHT · 83,576 FACES'
	}: { options?: ScanSceneOptions; poster?: string; hud?: string } = $props();

	let wrap: HTMLDivElement;
	let canvas: HTMLCanvasElement;
	let ready = $state(false);
	let scene: ScanScene | null = null;

	onMount(() => {
		if (motionDisabled()) return; // reduced-motion keeps the poster
		let disposed = false;
		let ro: ResizeObserver | undefined;
		let io: IntersectionObserver | undefined;

		async function init() {
			try {
				const mod = await import('$lib/three/ScanScene');
				if (disposed) return;
				scene = new mod.ScanScene(canvas, options);
				const size = () => {
					const r = wrap.getBoundingClientRect();
					scene?.resize(r.width, r.height);
				};
				ro = new ResizeObserver(size);
				ro.observe(wrap);
				size();
				await scene.ready;
				if (disposed) return;
				ready = true;
				io = new IntersectionObserver(
					([e]) => (e.isIntersecting ? scene?.start() : scene?.stop()),
					{
						threshold: 0.05
					}
				);
				io.observe(wrap);
			} catch (err) {
				console.warn('[hero-lab v3] scene init failed — keeping poster:', err);
				scene?.dispose();
				scene = null;
			}
		}

		const idle: (cb: () => void) => void =
			typeof window.requestIdleCallback === 'function'
				? (cb) => window.requestIdleCallback(cb)
				: (cb) => window.setTimeout(cb, 200);
		idle(init);

		return () => {
			disposed = true;
			ro?.disconnect();
			io?.disconnect();
			scene?.dispose();
			scene = null;
		};
	});
</script>

<div class="pass" bind:this={wrap}>
	<img
		class="pass__poster"
		class:is-hidden={ready}
		src={poster}
		alt=""
		loading="lazy"
		decoding="async"
	/>
	<!-- The scanner's light spilling into the room — behind the transparent
	     canvas, in front of the backdrop. Deliberately NOT synced to the
	     projector's phase: ambient bleed reads as the room, not the beam. -->
	<div class="pass__bleed" class:is-ready={ready} aria-hidden="true"></div>
	<canvas bind:this={canvas} class="pass__canvas" class:is-ready={ready} aria-hidden="true"
	></canvas>
	<p class="pass__hud">{hud}</p>
</div>

<style>
	.pass {
		position: absolute;
		inset: 0;
		background-color: var(--color-app-950);
		background-image:
			linear-gradient(rgba(70, 110, 180, 0.06) 1px, transparent 1px),
			linear-gradient(90deg, rgba(70, 110, 180, 0.06) 1px, transparent 1px);
		background-size: 48px 48px;
		background-position: center;
		pointer-events: none;
	}
	.pass__poster {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		/* A real frame of this scene (fender + crosshatch) — cover, so it lands
		   exactly where the canvas picks up, no letterbox seam. */
		object-fit: cover;
		opacity: 1;
		transition: opacity 500ms ease;
	}
	.pass__poster.is-hidden {
		opacity: 0;
	}
	.pass__canvas {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		transition: opacity 500ms ease;
	}
	.pass__canvas.is-ready {
		opacity: 1;
	}
	.pass__bleed {
		position: absolute;
		inset: 0;
		background:
			radial-gradient(46% 58% at 66% 46%, rgba(96, 84, 224, 0.17), transparent 70%),
			radial-gradient(80% 40% at 55% 100%, rgba(48, 60, 160, 0.12), transparent 75%);
		opacity: 0;
		transition: opacity 900ms ease;
		animation: pass-breathe 7s ease-in-out infinite;
	}
	.pass__bleed.is-ready {
		opacity: 1;
	}
	@keyframes pass-breathe {
		0%,
		100% {
			opacity: 0.75;
		}
		50% {
			opacity: 1;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.pass__bleed {
			animation: none;
		}
	}
	.pass__hud {
		position: absolute;
		right: var(--spacing-gutter);
		bottom: 1rem;
		z-index: 1; /* above the hero's bottom fade */
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.1em;
		color: rgba(140, 190, 255, 0.65);
	}
	/* Readout chrome earns its place on desktop only — on a phone it's noise. */
	@media (max-width: 720px) {
		.pass__hud {
			display: none;
		}
	}
</style>
